import React, { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faFileExcel, faTimes, faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useDownloadImportTemplateMutation, useImportUsersFromExcelMutation } from '../../../services/portal';
import { convertFileToBase64, generateTemplateFilename } from '../../../utils/fileUtils';
import FileUploadZone from './FileUploadZone';
import ImportOptions from './ImportOptions';
import ImportProgress from './ImportProgress';

/**
 * UserImport component for importing users from an Excel file.
 * @param {function} onImportComplete - Callback when import completes successfully.
 * @param {function} onClose - Callback to close the import dialog.
 */
const UserImport = ({ onImportComplete, onClose }) => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [sendEmails, setSendEmails] = useState(true);

  const [importUsers, { isLoading: uploading }] = useImportUsersFromExcelMutation();
  const [downloadTemplate] = useDownloadImportTemplateMutation();

  /**
   * Handles file selection from the upload zone.
   * @param {File} selectedFile - The selected file object.
   */
  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
  };

  /**
   * Downloads the Excel template for user import.
   * Shows a success or error toast based on a result. Create blob URL and download
   */
  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadTemplate().unwrap();

      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;

      link.download = generateTemplateFilename();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Failed to download template: ' + (error.data?.message || error.message));
    }
  };

  /**
   * Uploads the selected Excel file and starts the import process.
   * Convert file to Base64. Handle different result scenarios with safe property access
   * Shows progress and handles results/errors.
   */
  const handleUploadFile = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setProgress(0);

    try {
      const fileData = await convertFileToBase64(file);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await importUsers({
        fileData,
        fileName: file.name,
        sendEmails
      }).unwrap();

      clearInterval(progressInterval);
      setProgress(100);

      // Trigger the import complete callback with results
      if (onImportComplete) {
        onImportComplete(result);
      }

      const dbResults = result.results?.database;
      const validationResults = result.results?.validation;
      const successfulCount = dbResults?.successful?.length || 0;
      const errorCount = validationResults?.errors?.length || 0;
      const skippedCount = validationResults?.skipped?.length || 0;

      if (successfulCount > 0) {
        toast.success(`Successfully imported ${successfulCount} users`);
      } else if (errorCount > 0 || skippedCount > 0) {
        toast.warning(result.message || 'Import completed with validation issues');
      } else {
        toast.info(result.message || 'Import completed');
      }

    } catch (error) {
      console.error('Import error:', error);

      clearInterval(progressInterval);
      setProgress(0);

      let errorMessage = 'Import failed';
      if (error.data?.message) {
        errorMessage += ': ' + error.data.message;
      } else if (error.message) {
        errorMessage += ': ' + error.message;
      }

      if (error.data?.results && onImportComplete) {
        onImportComplete(error.data);
      }

      toast.error(errorMessage);
    } finally {
      setTimeout(() => setProgress(0), 2000);
    }
  };


  /**
   * Resets the form to the initial state for a new import.
   */
  const resetForm = () => {
    setFile(null);
  };


  return (
    <>
      <div
        style={{
          backgroundColor: '#0a183a',
          color: 'white',
          borderRadius: '15px',
          boxShadow: '0 0 10px rgba(116, 232, 199, 0.651)',
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          minHeight: '100%',
          padding: '20px'
        }}
      >
        {/* SVG Gradient Background */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            marginTop: '120px',
            padding: '30px'
          }}
        >
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="d-flex justify-content-between align-items-center mb-4">
             <h5 className="nucleus_title mb-0 d-flex align-items-center" style={{ color: 'white', fontSize: '20px', whiteSpace: 'nowrap' }}>
               <FontAwesomeIcon icon={faFileExcel} className="mr-2" size="sm" style={{ color: '#74E8C7' }} />
               Import Users from Excel
             </h5>
             <Button
               variant="link"
               size="sm"
               onClick={onClose}
               className="p-1 border-0 d-flex align-items-center justify-content-center"
               style={{
                 fontSize: '0.8rem',
                 color: '#74E8C7',
                 minHeight: '24px',
                 minWidth: '24px'
               }}
             >
               <FontAwesomeIcon icon={faTimes} size="sm" />
             </Button>
          </div>

          <div>
          <div className="mb-4">
            <h6 style={{ color: '#74E8C7', fontSize: '16px' }}>Step 1: Download Template</h6>
            <p style={{ color: '#d6d8e1' }}>
              Download the Excel template with the correct format and sample data.
            </p>
            <div className="d-flex justify-content-center">
               <Button
                 variant="primary"
                 onClick={handleDownloadTemplate}
                 className="mb-3 d-flex align-items-center justify-content-center btn2"
                 style={{
                   fontWeight: '600',
                   fontSize: '13px',
                   whiteSpace: 'nowrap'
                 }}
               >
                 <FontAwesomeIcon icon={faDownload} className="mr-2" size="sm" />
                 Download Template
               </Button>
            </div>
          </div>

          <FileUploadZone
            file={file}
            onFileSelect={handleFileSelect}
            onReset={resetForm}
          />

          <ImportOptions
            sendEmails={sendEmails}
            onSendEmailsChange={setSendEmails}
          />

          <ImportProgress
            progress={progress}
            isVisible={uploading}
          />

          <div className="d-flex justify-content-end">
             <Button
               variant="primary"
               onClick={handleUploadFile}
               disabled={!file || uploading}
               className="px-4 d-flex align-items-center justify-content-center btn2"
               style={{
                 fontWeight: '600',
                 fontSize: '13px',
                 whiteSpace: 'nowrap'
               }}
             >
               {uploading ? (
                 <>
                   <span className="spinner-border spinner-border-sm mr-2" style={{ width: '14px', height: '14px' }} />
                   Importing...
                 </>
               ) : (
                 <>
                   <FontAwesomeIcon icon={faUpload} className="mr-2" size="sm" />
                   Import Users
                 </>
               )}
             </Button>
          </div>
          </div>
        </div>
      </div>

    </>
  );
};

export default UserImport;
